import { useCallback, useEffect, useMemo, useRef } from 'react';

const defaultCleanParams = params => {
  const cleaned = {};
  for (const [key, value] of Object.entries(params)) {
    if (value === '' || value === null || value === undefined) continue;
    if (Array.isArray(value) && value.length === 0) continue;
    cleaned[key] = value;
  }
  return cleaned;
};

/**
 * Internal hook for managing AG Grid infinite row model state.
 * Handles datasource creation, cache purging on filter/sort changes, and loaded item tracking.
 *
 * @param {Object} options
 * @param {Function} options.fetchData - async (params) => { items, total, ...metadata }
 * @param {Object} options.params - Current params from useServerSideSearchParams (filters + pagination + sort)
 * @param {Function} [options.cleanParams] - Clean empty params before fetching (default: omit empty values)
 * @param {Function} [options.onDataLoad] - (items, total, metadata) => void
 * @param {Function} [options.onDataError] - (error) => void
 * @param {import('react').RefObject} options.tableRef - Ref to the Table component
 * @returns {{ datasource: import('ag-grid-community').IDatasource, purgeCache: () => void }}
 */
export function useServerSideInfiniteModel({
  fetchData,
  params,
  cleanParams = defaultCleanParams,
  onDataLoad,
  onDataError,
  tableRef,
}) {
  // Refs for stable datasource closure
  const paramsRef = useRef(params);
  useEffect(() => {
    paramsRef.current = params;
  }, [params]);

  const callbacksRef = useRef({ onDataLoad, onDataError, fetchData, cleanParams });
  useEffect(() => {
    callbacksRef.current = { onDataLoad, onDataError, fetchData, cleanParams };
  }, [onDataLoad, onDataError, fetchData, cleanParams]);

  // Stable datasource for AG Grid infinite row model
  const datasource = useMemo(
    () => ({
      getRows: async getRowsParams => {
        const { startRow, endRow, successCallback, failCallback } = getRowsParams;
        const pageSize = endRow - startRow;
        const page = Math.floor(startRow / pageSize) + 1;

        try {
          const { fetchData: fetch, cleanParams: clean, onDataLoad: onLoad } = callbacksRef.current;

          let apiParams = { ...paramsRef.current, page, pageSize };
          if (clean) apiParams = clean(apiParams);

          const response = await fetch(apiParams);

          if (response.error) {
            callbacksRef.current.onDataError?.(response.error);
            failCallback();
            return;
          }

          const { items, total, ...metadata } = response.data || response || {};
          const loadedItems = items || [];

          onLoad?.(loadedItems, total || 0, metadata);
          successCallback(loadedItems, total || 0);
        } catch (err) {
          callbacksRef.current.onDataError?.(err.message || 'Failed to fetch data');
          failCallback();
        }
      },
    }),
    // Stable - reads from refs
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [],
  );

  // Purge infinite cache when filter/sort params change (not page changes)
  const prevFilterSortKeyRef = useRef(null);
  useEffect(() => {
    const { page, pageSize, ...filterSort } = params;
    const filterSortKey = JSON.stringify(filterSort);

    if (prevFilterSortKeyRef.current !== null && prevFilterSortKeyRef.current !== filterSortKey) {
      const api = tableRef.current?.getApi?.();
      if (api) {
        api.purgeInfiniteCache();
      }
    }
    prevFilterSortKeyRef.current = filterSortKey;
  }, [params, tableRef]);

  // Helper to purge cache (used after mutations)
  const purgeCache = useCallback(() => {
    const api = tableRef.current?.getApi?.();
    if (api) {
      api.purgeInfiniteCache();
    }
  }, [tableRef]);

  return { datasource, purgeCache };
}
